import React, { useState } from 'react';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    crFile: null,
    ragioneSociale: '',
    email: '',
    codiceSdi: '',
    analisiTipo: 'standard',
    acceptTerms: false,
    acceptGdpr: false,
    acceptDigitalProduct: false
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // URL API Backend - Frankfurt
const API_URL = 'https://api.finanziamentisalento.com';

  // Tipi analisi con tempi stimati
  const tipiAnalisi = {
    rapida: { 
      nome: '⚡ Rapida',
      tempo: '1-2 minuti', 
      desc: 'Sintesi essenziale con rating, KPI principali e valutazione immediata'
    },
    standard: { 
      nome: '📊 Standard',
      tempo: '2-3 minuti', 
      desc: 'Analisi completa con trend storici, dettaglio intermediari e raccomandazioni'
    },
    approfondita: { 
      nome: '🔬 Approfondita',
      tempo: '5-8 minuti', 
      desc: 'Report professionale con scenari what-if, pattern interpretativi e strategie'
    }
  };

  // Prezzo fisso report completo
  const PREZZO_COMPLETO = 199;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData({ ...formData, crFile: file });
      setError(null);
    } else {
      setError('Per favore carica un file PDF valido');
      e.target.value = '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.crFile) {
      setError('Carica il file PDF della Centrale Rischi');
      return false;
    }
    if (!formData.ragioneSociale.trim()) {
      setError('Inserisci la ragione sociale');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Inserisci un indirizzo email valido');
      return false;
    }
    if (!formData.acceptTerms) {
      setError('Devi accettare i Termini e Condizioni per procedere');
      return false;
    }
    if (!formData.acceptGdpr) {
      setError('Il consenso GDPR è necessario per processare la richiesta');
      return false;
    }
    if (!formData.acceptDigitalProduct) {
      setError('Devi confermare la rinuncia al diritto di recesso per i prodotti digitali');
      return false;
    }
    return true;
  };

  const handleSubmitFree = async (e) => {
    e.preventDefault();
    
    // Per il gratuito servono solo file, ragione sociale, email e terms base
    if (!formData.crFile || !formData.ragioneSociale || !formData.email) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    if (!formData.acceptTerms) {
      setError('Devi accettare i Termini e Condizioni per procedere');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const data = new FormData();
    data.append('crFile', formData.crFile);
    data.append('ragioneSociale', formData.ragioneSociale);
    data.append('email', formData.email);
    data.append('reportType', 'tier1');
    data.append('analisiTipo', 'rapida');

    try {
      const response = await fetch(`${API_URL}/api/analyze-cr`, {
        method: 'POST',
        body: data
      });

      if (!response.ok) throw new Error('Errore nell\'analisi');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisi-cr-${formData.ragioneSociale.replace(/[^a-zA-Z0-9]/g, '-')}-gratuita.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setResult({
        type: 'free',
        message: 'Report gratuito scaricato con successo!'
      });
    } catch (err) {
      setError('Si è verificato un errore. Riprova.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDetailed = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    const data = new FormData();
    data.append('crFile', formData.crFile);
    data.append('ragioneSociale', formData.ragioneSociale);
    data.append('email', formData.email);
    data.append('codiceSdi', formData.codiceSdi || '');
    data.append('reportType', 'tier3');
    data.append('analisiTipo', formData.analisiTipo);
    data.append('acceptTerms', formData.acceptTerms);
    data.append('acceptGdpr', formData.acceptGdpr);
    data.append('acceptDigitalProduct', formData.acceptDigitalProduct);
    data.append('timestamp', new Date().toISOString());

    try {
      const response = await fetch(`${API_URL}/api/analyze-cr`, {
        method: 'POST',
        body: data
      });

      if (!response.ok) throw new Error('Errore nell\'analisi');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisi-cr-${formData.ragioneSociale.replace(/[^a-zA-Z0-9]/g, '-')}-completa.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setResult({
        type: 'detailed',
        message: 'Report completo scaricato con successo! (PDF + Excel + HTML)'
      });
    } catch (err) {
      setError('Si è verificato un errore. Riprova.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="logo-container">
          <img src="logo_check2.png" alt="FinanziamentiSalento" className="logo" />
        </div>
        <h1>FINANZIAMENTISALENTO.COM</h1>
        <p className="subtitle">Analisi Centrale dei Rischi</p>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          <div className="intro-section">
            <h2>📊 Analizza la tua Centrale dei Rischi</h2>
            <p>
              Carica il prospetto della Centrale Rischi Banca d'Italia e ottieni 
              un'analisi professionale immediata con rating, indicatori chiave e 
              raccomandazioni operative.
            </p>
          </div>

          {/* Form */}
          <form className="cr-form">
            {/* File Upload */}
            <div className="form-group">
              <label htmlFor="crFile">
                📄 Carica Centrale dei Rischi (PDF) *
              </label>
              <input
                type="file"
                id="crFile"
                accept=".pdf"
                onChange={handleFileChange}
                required
                className="file-input"
              />
              {formData.crFile && (
                <p className="file-name">✅ {formData.crFile.name}</p>
              )}
              <p className="form-hint">
                Richiedi la tua CR su <a href="https://arteweb.bancaditalia.it/arteweb-fe-web/cr" target="_blank" rel="noopener noreferrer">servizionline.bancaditalia.it</a> (gratuito)
              </p>
            </div>

            {/* Ragione Sociale */}
            <div className="form-group">
              <label htmlFor="ragioneSociale">
                🏢 Ragione Sociale Impresa *
              </label>
              <input
                type="text"
                id="ragioneSociale"
                name="ragioneSociale"
                value={formData.ragioneSociale}
                onChange={handleInputChange}
                placeholder="Es: NOME TUA AZIENDA S.R.L."
                required
                className="text-input"
              />
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">
                ✉️ Email per ricevere report *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="info@tuaazienda.it"
                required
                className="text-input"
              />
            </div>

            {/* Codice SDI */}
            <div className="form-group">
              <label htmlFor="codiceSdi">
                🧾 Codice SDI (per fatturazione elettronica)
              </label>
              <input
                type="text"
                id="codiceSdi"
                name="codiceSdi"
                value={formData.codiceSdi}
                onChange={handleInputChange}
                placeholder="Es: M5UXCR1 o 0000000"
                className="text-input"
                maxLength="7"
                style={{ textTransform: 'uppercase' }}
              />
              <p className="form-hint">
                Opzionale - 7 caratteri alfanumerici per ricevere fattura elettronica
              </p>
            </div>

            {/* Tipo Analisi */}
            <div className="form-group analisi-tipo-group">
              <label>🎯 Tipo di Analisi (per report completo)</label>
              <div className="analisi-options">
                
                <label className={`analisi-option ${formData.analisiTipo === 'rapida' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="analisiTipo"
                    value="rapida"
                    checked={formData.analisiTipo === 'rapida'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-name">{tipiAnalisi.rapida.nome}</span>
                      <span className="option-time">⏱️ {tipiAnalisi.rapida.tempo}</span>
                    </div>
                    <p className="option-desc">{tipiAnalisi.rapida.desc}</p>
                  </div>
                </label>

                <label className={`analisi-option recommended ${formData.analisiTipo === 'standard' ? 'selected' : ''}`}>
                  <span className="recommended-badge">⭐ Consigliata</span>
                  <input
                    type="radio"
                    name="analisiTipo"
                    value="standard"
                    checked={formData.analisiTipo === 'standard'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-name">{tipiAnalisi.standard.nome}</span>
                      <span className="option-time">⏱️ {tipiAnalisi.standard.tempo}</span>
                    </div>
                    <p className="option-desc">{tipiAnalisi.standard.desc}</p>
                  </div>
                </label>

                <label className={`analisi-option ${formData.analisiTipo === 'approfondita' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="analisiTipo"
                    value="approfondita"
                    checked={formData.analisiTipo === 'approfondita'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-name">{tipiAnalisi.approfondita.nome}</span>
                      <span className="option-time">⏱️ {tipiAnalisi.approfondita.tempo}</span>
                    </div>
                    <p className="option-desc">{tipiAnalisi.approfondita.desc}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Consensi Section */}
            <div className="consensi-section">
              <h3>📋 Consensi e Autorizzazioni</h3>
              
              {/* Terms & Conditions */}
              <div className="consent-group">
                <label className="consent-label">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="consent-text">
                    Ho letto ed accetto i{' '}
                    <a href="https://finanziamentisalento.com/termini-e-condizioni/" target="_blank" rel="noopener noreferrer">
                      Termini e Condizioni
                    </a>, la{' '}
                    <a href="https://finanziamentisalento.com/privacy-policy-oxy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a> e il{' '}
                    <a href="https://finanziamentisalento.com/disclaimer/" target="_blank" rel="noopener noreferrer">
                      Disclaimer
                    </a> di questo sito web. <span className="required">*</span>
                  </span>
                </label>
              </div>

              {/* GDPR Consent */}
              <div className="consent-group">
                <label className="consent-label">
                  <input
                    type="checkbox"
                    name="acceptGdpr"
                    checked={formData.acceptGdpr}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="consent-text">
                    Acconsento all'utilizzo di tecnologie automatizzate (email, SMS, telefono) 
                    per essere contattato relativamente a richieste di finanziamento e servizi 
                    correlati, per 36 mesi. Posso revocare il consenso scrivendo a{' '}
                    <a href="mailto:privacy@finanziamentisalento.com">privacy@finanziamentisalento.com</a>. <span className="required">*</span>
                  </span>
                </label>
                <div className="consent-info-box">
                  <strong>ℹ️ Perché questo consenso?</strong>
                  <p>Ci permette di: rispondere alle tue richieste, fornirti assistenza personalizzata, 
                  garantire tempi di risposta rapidi. Senza questo consenso non potremo processare la richiesta.</p>
                </div>
              </div>

              {/* Digital Product Consent */}
              <div className="consent-group digital-consent">
                <label className="consent-label">
                  <input
                    type="checkbox"
                    name="acceptDigitalProduct"
                    checked={formData.acceptDigitalProduct}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="consent-text">
                    <strong>Prodotto Digitale - Rinuncia al Diritto di Recesso</strong><br />
                    Confermo di voler ricevere immediatamente il report di analisi e riconosco che 
                    <strong> perderò il diritto di recesso</strong> una volta iniziata l'esecuzione, 
                    ai sensi dell'art. 59, comma 1, lett. o) del D.Lgs. 206/2005 (Codice del Consumo). <span className="required">*</span>
                  </span>
                </label>
                <div className="legal-info-box">
                  <strong>📜 Normativa di Riferimento</strong>
                  <p>
                    Ai sensi dell'art. 59, comma 1, lett. o) del Codice del Consumo, il diritto di recesso 
                    non si applica alla fornitura di contenuto digitale non fornito su supporto materiale 
                    se l'esecuzione è iniziata con l'accordo espresso del consumatore.
                  </p>
                  <p>
                    <strong>Conseguenza:</strong> Una volta avviata la generazione del report, 
                    non sarà possibile richiedere il rimborso.
                  </p>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {result && (
              <div className="success-message">
                ✅ {result.message}
              </div>
            )}

            {/* Buttons */}
            <div className="button-group">
              <button
                type="button"
                onClick={handleSubmitFree}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? '⏳ Elaborazione...' : '📄 Report Gratuito (PDF)'}
              </button>

              <button
                type="button"
                onClick={handleSubmitDetailed}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? '⏳ Elaborazione...' : `🚀 Report Completo - €${PREZZO_COMPLETO}`}
              </button>
            </div>

            <p className="form-note">
              🔒 Pagamento sicuro con Stripe | 💳 Carte, Bonifico, Apple Pay, Google Pay
            </p>
          </form>

          {/* CTA Section */}
          <div className="cta-section">
            <h3>💡 Vuoi una consulenza personalizzata?</h3>
            <p>
              Dopo aver analizzato il report, prenota una consulenza gratuita 
              di 30 minuti per discutere delle strategie di miglioramento.
            </p>
            <a href="https://finanziamentisalento.com/contattaci/" className="btn btn-cta" target="_blank" rel="noopener noreferrer">
              📞 Prenota Consulenza Gratuita
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <p>Analisi a cura di <strong>FinanziamentiSalento.com</strong></p>
          <p>
            📞 +39 XXX XXX XXXX | 
            ✉️ agevolazioni@finanziamentisalento.com | 
            🌐 www.finanziamentisalento.com
          </p>
          <div className="footer-links">
            <a href="https://finanziamentisalento.com/privacy-policy-oxy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <a href="https://finanziamentisalento.com/termini-e-condizioni/" target="_blank" rel="noopener noreferrer">Termini</a>
            <a href="https://finanziamentisalento.com/disclaimer/" target="_blank" rel="noopener noreferrer">Disclaimer</a>
          </div>
          <p className="disclaimer">
            Le analisi fornite hanno carattere informativo. Non costituiscono 
            consulenza finanziaria specifica.
          </p>
        </div>
      </footer>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Analisi in corso...</p>
            <p className="loading-hint">
              Tempo stimato: {tipiAnalisi[formData.analisiTipo].tempo}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
